import { useState, useEffect } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import { Trophy, Users, Calendar, ChevronRight, Crown, Medal, Clock, Plus, Sparkles, Timer } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardHeader, CardTitle, CardDescription, CardContent, CardFooter } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Skeleton } from '../components/ui/skeleton';
import { toast } from 'sonner';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import { parseISO, differenceInSeconds } from 'date-fns';
import { getToken } from '../lib/auth';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Featured Countdown Hero Component
const FeaturedCountdownHero = ({ competition, onJoin, isJoined, joining }) => {
    const [timeLeft, setTimeLeft] = useState({ days: 0, hours: 0, minutes: 0, seconds: 0 });
    const [competitionState, setCompetitionState] = useState('countdown'); // countdown, live, completed
    const [currentDay, setCurrentDay] = useState(null);
    const navigate = useNavigate();

    useEffect(() => {
        if (!competition) return;
        
        const updateState = () => {
            const now = new Date();
            const startDate = new Date(competition.start_date);
            const endDate = new Date(competition.end_date);
            endDate.setHours(23, 59, 59, 999); // End of end date
            
            // Check if competition status is explicitly completed/complete
            if (competition.status === 'completed' || competition.status === 'complete') {
                setCompetitionState('completed');
                return;
            }
            
            // Check if competition is completed based on end date
            if (now > endDate) {
                setCompetitionState('completed');
                return;
            }
            
            // Check if competition has started (we're past the start date)
            const startOfStartDate = new Date(startDate);
            startOfStartDate.setHours(0, 0, 0, 0);
            
            if (now >= startOfStartDate) {
                setCompetitionState('live');
                
                // Calculate which day we're on
                if (competition.days && competition.days.length > 0) {
                    // Find current day based on dates
                    const todayStr = now.toISOString().split('T')[0];
                    const matchingDay = competition.days.find(d => d.date === todayStr);
                    if (matchingDay) {
                        setCurrentDay(matchingDay.day_number);
                    } else {
                        // Calculate day number from start date
                        const daysDiff = Math.floor((now - startOfStartDate) / (1000 * 60 * 60 * 24)) + 1;
                        setCurrentDay(Math.min(daysDiff, competition.days.length));
                    }
                } else {
                    // Single day competition
                    setCurrentDay(1);
                }
                return;
            }
            
            // Still in countdown mode
            setCompetitionState('countdown');
            
            // Calculate countdown to featured_countdown_date or start_date
            const countdownTarget = competition.featured_countdown_date 
                ? parseISO(competition.featured_countdown_date)
                : startOfStartDate;
            
            const diff = differenceInSeconds(countdownTarget, now);
            
            if (diff <= 0) {
                setTimeLeft({ days: 0, hours: 0, minutes: 0, seconds: 0 });
                return;
            }
            
            const days = Math.floor(diff / 86400);
            const hours = Math.floor((diff % 86400) / 3600);
            const minutes = Math.floor((diff % 3600) / 60);
            const seconds = diff % 60;
            
            setTimeLeft({ days, hours, minutes, seconds });
        };

        updateState();
        const interval = setInterval(updateState, 1000);
        return () => clearInterval(interval);
    }, [competition]);

    if (!competition) return null;

    // Get day name for display
    const getDayName = () => {
        if (!currentDay) return '';
        if (competition.days && competition.days.length > 0) {
            const day = competition.days.find(d => d.day_number === currentDay);
            if (day?.meeting_name) return day.meeting_name;
        }
        return `Day ${currentDay}`;
    };

    return (
        <div className="relative mb-8 rounded-2xl overflow-hidden bg-gradient-to-br from-primary/20 via-primary/10 to-background border border-primary/30">
            {/* Background pattern */}
            <div className="absolute inset-0 opacity-5">
                <div className="absolute inset-0" style={{
                    backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='0.4'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`
                }} />
            </div>

            <div className="relative p-8 md:p-12">
                {/* Header badge */}
                <div className="flex items-center gap-2 mb-6">
                    {competitionState === 'countdown' && (
                        <Badge className="bg-amber-500/20 text-amber-400 border-amber-500/30 px-3 py-1">
                            <Sparkles className="h-3 w-3 mr-1" />
                            FEATURED EVENT
                        </Badge>
                    )}
                    {competitionState === 'live' && (
                        <Badge className="bg-green-500/20 text-green-400 border-green-500/30 px-3 py-1 animate-pulse">
                            <Sparkles className="h-3 w-3 mr-1" />
                            LIVE NOW
                        </Badge>
                    )}
                    {competitionState === 'completed' && (
                        <Badge className="bg-purple-500/20 text-purple-400 border-purple-500/30 px-3 py-1">
                            <Trophy className="h-3 w-3 mr-1" />
                            COMPLETED
                        </Badge>
                    )}
                </div>

                {/* Title - changes based on state */}
                {competitionState === 'countdown' && (
                    <h1 className="font-heading text-4xl md:text-5xl lg:text-6xl font-bold mb-4 uppercase tracking-wide page-title">
                        {competition.name}
                    </h1>
                )}
                {competitionState === 'live' && (
                    <>
                        <p className="text-lg text-primary mb-2 font-semibold uppercase tracking-wider">
                            Welcome to {getDayName()}
                        </p>
                        <h1 className="font-heading text-4xl md:text-5xl lg:text-6xl font-bold mb-4 uppercase tracking-wide page-title">
                            {competition.name}
                        </h1>
                    </>
                )}
                {competitionState === 'completed' && (
                    <>
                        <p className="text-lg text-purple-400 mb-2 font-semibold uppercase tracking-wider">
                            Thanks for competing!
                        </p>
                        <h1 className="font-heading text-4xl md:text-5xl lg:text-6xl font-bold mb-4 uppercase tracking-wide page-title">
                            {competition.name}
                        </h1>
                    </>
                )}

                {/* Description - only show in countdown mode */}
                {competitionState === 'countdown' && competition.featured_description && (
                    <p className="text-lg md:text-xl text-muted-foreground max-w-2xl mb-8 leading-relaxed">
                        {competition.featured_description}
                    </p>
                )}

                {/* Live mode message */}
                {competitionState === 'live' && (
                    <p className="text-lg md:text-xl text-muted-foreground max-w-2xl mb-8 leading-relaxed">
                        The action is underway! Make your picks and climb the leaderboard.
                    </p>
                )}

                {/* Completed mode message */}
                {competitionState === 'completed' && (
                    <p className="text-lg md:text-xl text-muted-foreground max-w-2xl mb-8 leading-relaxed">
                        The competition has ended. Check out the final standings!
                    </p>
                )}

                {/* Countdown Timer - only in countdown mode */}
                {competitionState === 'countdown' && (
                    <div className="mb-8">
                        <p className="text-sm text-muted-foreground mb-3 flex items-center gap-2">
                            <Timer className="h-4 w-4" />
                            COUNTDOWN TO RACE
                        </p>
                        <div className="flex gap-4 md:gap-6">
                            {[
                                { value: timeLeft.days, label: 'DAYS' },
                                { value: timeLeft.hours, label: 'HRS' },
                                { value: timeLeft.minutes, label: 'MIN' },
                                { value: timeLeft.seconds, label: 'SEC' },
                            ].map((item, idx) => (
                                <div key={idx} className="text-center">
                                    <div className="bg-background/80 backdrop-blur-sm border border-primary/30 rounded-xl px-4 py-3 md:px-6 md:py-4 min-w-[70px] md:min-w-[90px]">
                                        <span className="font-data text-3xl md:text-4xl lg:text-5xl font-bold text-primary">
                                            {String(item.value).padStart(2, '0')}
                                        </span>
                                    </div>
                                    <p className="text-xs md:text-sm text-muted-foreground mt-2 font-medium">{item.label}</p>
                                </div>
                            ))}
                        </div>
                    </div>
                )}

                {/* Day indicator for live mode */}
                {competitionState === 'live' && competition.days && competition.days.length > 1 && (
                    <div className="mb-8">
                        <div className="flex gap-2">
                            {competition.days.map((day) => (
                                <div 
                                    key={day.day_number}
                                    className={`px-4 py-2 rounded-lg text-sm font-medium ${
                                        day.day_number === currentDay 
                                            ? 'bg-primary text-primary-foreground' 
                                            : day.day_number < currentDay 
                                                ? 'bg-muted/50 text-muted-foreground' 
                                                : 'bg-muted/30 text-muted-foreground/50'
                                    }`}
                                >
                                    Day {day.day_number}
                                </div>
                            ))}
                        </div>
                    </div>
                )}

                {/* Stats & Join */}
                <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4">
                    {isJoined ? (
                        <Button 
                            size="lg" 
                            className="btn-pill gap-2 bg-green-600 hover:bg-green-700 text-white"
                            onClick={() => navigate(`/competitions/${competition.id}`)}
                            data-testid="view-featured-btn"
                        >
                            <Medal className="h-5 w-5" />
                            View Competition
                            <ChevronRight className="h-4 w-4" />
                        </Button>
                    ) : (
                        <Button 
                            size="lg" 
                            className="btn-pill gap-2 bg-amber-500 hover:bg-amber-600 text-black"
                            onClick={onJoin}
                            disabled={joining}
                            data-testid="join-featured-btn"
                        >
                            <Trophy className="h-5 w-5" />
                            {joining ? 'Joining...' : 'Join Competition'}
                        </Button>
                    )}
                    
                    <div className="flex items-center gap-4 text-sm text-muted-foreground">
                        <span className="flex items-center gap-1.5">
                            <Users className="h-4 w-4" />
                            <span className="font-semibold text-foreground">{competition.entry_count || 0}</span> entries
                        </span>
                        <span className="flex items-center gap-1.5">
                            <Calendar className="h-4 w-4" />
                            {new Date(competition.start_date).toLocaleDateString('en-GB', { 
                                day: 'numeric', 
                                month: 'short',
                                year: 'numeric'
                            })}
                        </span>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default function CompetitionsPage() {
    const { inviteCode } = useParams();
    const { user, isAuthenticated } = useAuth();
    const navigate = useNavigate();
    const [competitions, setCompetitions] = useState([]);
    const [featuredCountdown, setFeaturedCountdown] = useState(null);
    const [inviteCompetition, setInviteCompetition] = useState(null);
    const [loading, setLoading] = useState(true);
    const [joining, setJoining] = useState(false);

    const fetchCompetitions = async () => {
        try {
            const token = getToken();
            const res = await axios.get(`${API_URL}/api/competitions`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            setCompetitions(res.data.competitions || []);
            setFeaturedCountdown(res.data.featured_countdown || null);
        } catch (err) {
            console.error('Failed to fetch competitions:', err);
            toast.error('Failed to load competitions');
        } finally {
            setLoading(false);
        }
    };

    const fetchInviteCompetition = async () => {
        try {
            const res = await axios.get(`${API_URL}/api/competitions/join/${inviteCode}`);
            if (res.data.competition.is_festival) {
                // Festival competitions collect name/company through their own
                // form — send old/shared invite links there instead.
                navigate(`/festival/join/${inviteCode}`, { replace: true });
                return;
            }
            setInviteCompetition(res.data.competition);
        } catch (err) {
            console.error('Failed to fetch competition:', err);
            toast.error(err.response?.data?.detail || 'Invalid invite link');
            navigate('/competitions');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (inviteCode) {
            fetchInviteCompetition();
        } else if (isAuthenticated) {
            fetchCompetitions();
        } else {
            setLoading(false);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [inviteCode, isAuthenticated]);

    const handleJoin = async () => {
        if (!isAuthenticated) {
            toast.info('Please sign in or register to join the competition');
            navigate(`/register?redirect=/competitions/join/${inviteCode}`);
            return;
        }

        setJoining(true);
        try {
            const token = getToken();
            await axios.post(`${API_URL}/api/competitions/join/${inviteCode}`, {}, {
                headers: { Authorization: `Bearer ${token}` }
            });
            toast.success('Successfully joined the competition!');
            navigate(`/competitions/${inviteCompetition.id}`);
        } catch (err) {
            console.error('Failed to join:', err);
            toast.error(err.response?.data?.detail || 'Failed to join competition');
        } finally {
            setJoining(false);
        }
    };

    const handleJoinFeatured = async () => {
        if (!featuredCountdown?.invite_code) return;
        
        setJoining(true);
        try {
            const token = getToken();
            await axios.post(`${API_URL}/api/competitions/join/${featuredCountdown.invite_code}`, {}, {
                headers: { Authorization: `Bearer ${token}` }
            });
            toast.success('Successfully joined the competition!');
            navigate(`/competitions/${featuredCountdown.id}`);
        } catch (err) {
            console.error('Failed to join:', err);
            toast.error(err.response?.data?.detail || 'Failed to join competition');
        } finally {
            setJoining(false);
        }
    };

    const getStatusBadge = (status) => {
        const styles = {
            draft: 'bg-gray-500/20 text-gray-400',
            open: 'bg-green-500/20 text-green-400',
            in_progress: 'bg-blue-500/20 text-blue-400',
            completed: 'bg-purple-500/20 text-purple-400'
        };
        return <Badge className={styles[status] || styles.draft}>{status?.replace('_', ' ')}</Badge>;
    };

    // Invite link join page
    if (inviteCode && inviteCompetition) {
        return (
            <div className="container-app py-8">
                <div className="max-w-lg mx-auto">
                    <Card className="border-primary/20 bg-gradient-to-b from-primary/5 to-transparent">
                        <CardHeader className="text-center pb-4">
                            <div className="mx-auto w-16 h-16 rounded-full bg-primary/20 flex items-center justify-center mb-4">
                                <Trophy className="h-8 w-8 text-primary" />
                            </div>
                            <CardTitle className="text-2xl font-heading">{inviteCompetition.name}</CardTitle>
                            <CardDescription className="text-base">
                                {inviteCompetition.description || 'You\'ve been invited to join this tipping competition!'}
                            </CardDescription>
                        </CardHeader>
                        <CardContent className="space-y-4">
                            <div className="grid grid-cols-2 gap-4 text-center">
                                <div className="p-4 rounded-lg bg-muted/50">
                                    <Calendar className="h-5 w-5 mx-auto mb-2 text-muted-foreground" />
                                    <p className="text-sm text-muted-foreground">Meeting</p>
                                    <p className="font-semibold">{inviteCompetition.meeting_name}</p>
                                </div>
                                <div className="p-4 rounded-lg bg-muted/50">
                                    <Users className="h-5 w-5 mx-auto mb-2 text-muted-foreground" />
                                    <p className="text-sm text-muted-foreground">Entries</p>
                                    <p className="font-semibold">{inviteCompetition.entry_count || 0}</p>
                                </div>
                            </div>
                            <div className="p-4 rounded-lg bg-muted/50">
                                <p className="text-sm text-muted-foreground mb-1">Competition Dates</p>
                                <p className="font-semibold">
                                    {new Date(inviteCompetition.start_date).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })} - {new Date(inviteCompetition.end_date).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}
                                </p>
                            </div>
                            <div className="p-4 rounded-lg border border-amber-500/30 bg-amber-500/10">
                                <p className="text-sm text-amber-400 flex items-center gap-2">
                                    <Clock className="h-4 w-4" />
                                    Picks must be submitted {inviteCompetition.deadline_minutes} minutes before each race
                                </p>
                            </div>
                        </CardContent>
                        <CardFooter className="flex flex-col gap-3">
                            <Button 
                                className="w-full btn-pill" 
                                size="lg"
                                onClick={handleJoin}
                                disabled={joining}
                                data-testid="join-competition-btn"
                            >
                                {joining ? 'Joining...' : isAuthenticated ? 'Join Competition' : 'Sign Up to Join'}
                            </Button>
                            {!isAuthenticated && (
                                <p className="text-sm text-muted-foreground text-center">
                                    Already have an account?{' '}
                                    <Link to={`/login?redirect=/competitions/join/${inviteCode}`} className="text-primary hover:underline">
                                        Sign in
                                    </Link>
                                </p>
                            )}
                        </CardFooter>
                    </Card>
                </div>
            </div>
        );
    }

    // Main competitions list
    return (
        <div className="container-app py-8">
            {/* Featured Countdown Hero */}
            {featuredCountdown && (
                <FeaturedCountdownHero 
                    competition={featuredCountdown}
                    onJoin={handleJoinFeatured}
                    isJoined={featuredCountdown.is_joined}
                    joining={joining}
                />
            )}

            <div className="flex items-center justify-between mb-8">
                <div>
                    <h1 className="text-3xl font-heading font-bold mb-2 uppercase page-title">Tipping Competitions</h1>
                    <p className="text-muted-foreground">Pick winners and compete against other tipsters</p>
                </div>
                {user?.is_admin && (
                    <Link to="/admin/competitions">
                        <Button variant="outline" className="gap-2">
                            <Plus className="h-4 w-4" />
                            Manage Competitions
                        </Button>
                    </Link>
                )}
            </div>

            {loading ? (
                <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                    {[1, 2, 3].map(i => (
                        <Card key={i}>
                            <CardHeader>
                                <Skeleton className="h-6 w-3/4" />
                                <Skeleton className="h-4 w-1/2" />
                            </CardHeader>
                            <CardContent>
                                <Skeleton className="h-20 w-full" />
                            </CardContent>
                        </Card>
                    ))}
                </div>
            ) : !isAuthenticated ? (
                <Card className="max-w-md mx-auto text-center">
                    <CardHeader>
                        <Trophy className="h-12 w-12 mx-auto mb-4 text-primary" />
                        <CardTitle>Sign in to View Competitions</CardTitle>
                        <CardDescription>
                            Join tipping competitions, make your picks, and compete for the top spot on the leaderboard.
                        </CardDescription>
                    </CardHeader>
                    <CardFooter className="justify-center">
                        <Link to="/login?redirect=/competitions">
                            <Button className="btn-pill">Sign In</Button>
                        </Link>
                    </CardFooter>
                </Card>
            ) : competitions.length === 0 ? (
                <Card className="max-w-md mx-auto text-center">
                    <CardHeader>
                        <Trophy className="h-12 w-12 mx-auto mb-4 text-muted-foreground" />
                        <CardTitle>No Competitions Available</CardTitle>
                        <CardDescription>
                            There are no active competitions at the moment. Check back soon or use an invite link to join one.
                        </CardDescription>
                    </CardHeader>
                </Card>
            ) : (
                <div className="space-y-8">
                    {/* Active/Upcoming Competitions - exclude featured competition and hidden ones */}
                    {(() => {
                        const activeComps = competitions.filter(c => 
                            c.status !== 'complete' && 
                            c.status !== 'completed' &&
                            !(featuredCountdown && c.id === featuredCountdown.id) && // Hide featured from list
                            (!c.is_hidden || c.is_joined) // Hide hidden competitions unless user has joined
                        );
                        
                        if (activeComps.length === 0) return null;
                        
                        return (
                            <div>
                                <h2 className="text-xl font-semibold mb-4 flex items-center gap-2">
                                    <Clock className="h-5 w-5 text-primary" />
                                    Active Competitions
                                </h2>
                                <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                                    {activeComps.map(comp => (
                                        <Card 
                                            key={comp.id} 
                                            className={`hover:border-primary/50 transition-colors ${comp.is_featured ? 'border-primary/30 bg-gradient-to-b from-primary/5 to-transparent' : ''}`}
                                            data-testid={`competition-card-${comp.id}`}
                                        >
                                            <CardHeader>
                                                <div className="flex items-start justify-between">
                                                    <div>
                                                        <CardTitle className="flex items-center gap-2">
                                                            {comp.is_featured && <Crown className="h-4 w-4 text-amber-400" />}
                                                            {comp.name}
                                                        </CardTitle>
                                                        <CardDescription>{comp.meeting_name}</CardDescription>
                                                    </div>
                                                    {getStatusBadge(comp.status)}
                                                </div>
                                            </CardHeader>
                                            <CardContent>
                                                <div className="flex items-center gap-4 text-sm text-muted-foreground mb-4">
                                                    <span className="flex items-center gap-1">
                                                        <Users className="h-4 w-4" />
                                                        {comp.entry_count} entries
                                                    </span>
                                                    <span className="flex items-center gap-1">
                                                        <Calendar className="h-4 w-4" />
                                                        {new Date(comp.start_date).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })}
                                                    </span>
                                                </div>
                                                {comp.is_joined ? (
                                                    <Badge variant="outline" className="bg-green-500/10 text-green-400 border-green-500/30">
                                                        <Medal className="h-3 w-3 mr-1" />
                                                        Entered
                                                    </Badge>
                                                ) : (
                                                    <Badge variant="outline">Not Entered</Badge>
                                                )}
                                            </CardContent>
                                            <CardFooter>
                                                <Link to={`/competitions/${comp.id}`} className="w-full">
                                                    <Button variant="outline" className="w-full gap-2">
                                                        {comp.is_joined ? 'View Competition' : 'View Details'}
                                                        <ChevronRight className="h-4 w-4" />
                                                    </Button>
                                                </Link>
                                            </CardFooter>
                                        </Card>
                                    ))}
                                </div>
                            </div>
                        );
                    })()}
                    
                    {/* Completed Competitions - also filter hidden ones */}
                    {competitions.filter(c => (c.status === 'complete' || c.status === 'completed') && (!c.is_hidden || c.is_joined)).length > 0 && (
                        <div>
                            <h2 className="text-xl font-semibold mb-4 flex items-center gap-2 text-muted-foreground">
                                <Trophy className="h-5 w-5" />
                                Past Competitions
                            </h2>
                            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                                {competitions.filter(c => (c.status === 'complete' || c.status === 'completed') && (!c.is_hidden || c.is_joined)).map(comp => (
                                    <Card 
                                        key={comp.id} 
                                        className="opacity-75 hover:opacity-100 transition-opacity"
                                        data-testid={`competition-card-${comp.id}`}
                                    >
                                        <CardHeader>
                                            <div className="flex items-start justify-between">
                                                <div>
                                                    <CardTitle className="flex items-center gap-2">
                                                        {comp.name}
                                                    </CardTitle>
                                                    <CardDescription>{comp.meeting_name}</CardDescription>
                                                </div>
                                                <Badge className="bg-muted text-muted-foreground">Finished</Badge>
                                            </div>
                                        </CardHeader>
                                        <CardContent>
                                            <div className="flex items-center gap-4 text-sm text-muted-foreground mb-4">
                                                <span className="flex items-center gap-1">
                                                    <Users className="h-4 w-4" />
                                                    {comp.entry_count} entries
                                                </span>
                                                <span className="flex items-center gap-1">
                                                    <Calendar className="h-4 w-4" />
                                                    {new Date(comp.start_date).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })}
                                                </span>
                                            </div>
                                            {comp.is_joined && (
                                                <Badge variant="outline" className="bg-green-500/10 text-green-400 border-green-500/30">
                                                    <Medal className="h-3 w-3 mr-1" />
                                                    Participated
                                                </Badge>
                                            )}
                                        </CardContent>
                                        <CardFooter>
                                            <Link to={`/competitions/${comp.id}`} className="w-full">
                                                <Button variant="ghost" className="w-full gap-2">
                                                    View Results
                                                    <ChevronRight className="h-4 w-4" />
                                                </Button>
                                            </Link>
                                        </CardFooter>
                                    </Card>
                                ))}
                            </div>
                        </div>
                    )}
                    
                    {/* Show message if only completed competitions exist */}
                    {competitions.filter(c => c.status !== 'complete' && c.status !== 'completed' && (!c.is_hidden || c.is_joined)).length === 0 && (
                        <Card className="max-w-md mx-auto text-center">
                            <CardHeader>
                                <Clock className="h-12 w-12 mx-auto mb-4 text-muted-foreground" />
                                <CardTitle>No Active Competitions</CardTitle>
                                <CardDescription>
                                    All current competitions have finished. Check back soon for new ones!
                                </CardDescription>
                            </CardHeader>
                        </Card>
                    )}
                </div>
            )}
        </div>
    );
}
